import { Component, inject, viewChild, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { VoucherNursingCarePlanDetailPageStore } from './voucher-nursing-care-plan-detail-page.store';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { isInvalid } from '../../../../shared/helpers/validate';
import {
    VoucherNursingCareAssessmentGridItemObject,
    VoucherNursingCareDiagnosisGridItemObject,
    VoucherObject,
} from '@it2go/types';
import {
    VoucherNursingCarePlanUpdateInput,
} from '@libs/types/src/services/input-type/voucher/voucher-nursing-care-plan.update-input';
import { FilterBuilder } from '../../../helper/filter.builder';
import { FilterInput } from '@it2go/data-grid';
import { TableColumn } from '../../../../shared/components/kit/table/table.types';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';
import { BreadcrumbService } from 'xng-breadcrumb';
import { BreadcrumbsEnum } from '../../../../shared/models/breadcrumbs.enum';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SessionService } from 'packages/client/src/app/services/session.service';
import { localDate } from '../../../../shared/pipes/local-date.pipe';
import { translate } from '../../../../shared/shared.global-service';
import {
    NursingCareDiagnosisUpsertDialogComponent,
} from './nursing-care-diagnosis-upsert-dialog/nursing-care-diagnosis-upsert-dialog.component';
import {
    NursingCareAssessmentUpsertDialogComponent,
} from './nursing-care-assessment-upsert-dialog/nursing-care-assessment-upsert-dialog.component';
import { customLengthTextField, dateColumn } from '../../../../shared/components/kit/table/table.fields';
import { VoucherNursingCarePlanDetailPageStore2 } from './voucher-nursing-care-plan-detail-page.store2';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { successDeleted } from '../../../content.error';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { InputWysiwygComponent } from '../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { Card } from '@openng/optimus-ui/card';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@openng/optimus-ui/tabs';
import { AsyncPipe } from '@angular/common';
import { ErrorFloatMessageComponent } from '../../../../../components/kit/float-message/error-float-message.component';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';

@Component({
    selector: 'app-voucher-nursing-care-plan-detail-page',
    templateUrl: './voucher-nursing-care-plan-detail-page.component.html',
    styleUrl: './voucher-nursing-care-plan-detail-page.component.scss',
    providers: [VoucherNursingCarePlanDetailPageStore, VoucherNursingCarePlanDetailPageStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        Card,
        Tabs,
        TabList,
        Tab,
        TranslatePipe,
        TabPanels,
        TabPanel,
        RouterLink,
        InputWysiwygComponent,
        TableComponent,
        AsyncPipe,
        ErrorFloatMessageComponent,
        ActionButtonsComponent,
    ],
})
export class VoucherNursingCarePlanDetailPageComponent extends SubscriptionComponent {

    public dialogService = inject(BaseDialogService);
    public confirmBuilder = inject(ConfirmBuilder);
    protected readonly store = inject(VoucherNursingCarePlanDetailPageStore);
    protected readonly store2 = inject(VoucherNursingCarePlanDetailPageStore2);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly fb = inject(FormBuilder);
    private readonly breadcrumbService = inject(BreadcrumbService);
    private readonly translateService = inject(TranslateService);
    private readonly session = inject(SessionService);


    public readonly wysiwyg = viewChild<InputWysiwygComponent>(InputWysiwygComponent);

    formSummary = this.fb.group({
        summary: this.fb.control(''),
    });

    folderId: string | null = null;
    voucherId: number | null = null;
    voucher: VoucherObject | null = null;

    diagnosisLinkTitle = '';
    title = '';

    invalidForm = false;

    visibleMainDiagnosis = false;
    additionalFilterVoucherId: FilterInput[][] = [[]];

    @ViewChild('diagnosesTable') diagnosesTable!: TableComponent;
    @ViewChild('assessmentsTable') assessmentsTable!: TableComponent;

    diagnosisColumns: TableColumn[] = [
        { name: 'code', sortable: true, filterable: true },
        { name: 'title', filterable: true },
        { name: 'groupName', filterable: true },
        customLengthTextField('description', 150),
    ];

    assessmentColumns: TableColumn[] = [
        dateColumn,
        customLengthTextField('assessment', 150),
    ];

    constructor() {
        super();
        this.store.doOnActions(
            () => this.diagnosesTable.reload(),
            'createNursingCareDiagnosis',
            'updateNursingCareDiagnosis',
        );
        this.store.doOnActions(
            () => this.assessmentsTable.reload(),
            'createNursingCareAssessment',
            'updateNursingCareAssessment',
        );

        this.subs.push(
            this.route.params.subscribe((params) => {
                this.voucherId = Number(params.voucherId);
                this.additionalFilterVoucherId = this.voucherId ? [[{
                    column: 'voucherNursingCareId',
                    operator: 'EQ',
                    value: [this.voucherId.toString()],
                }]] : [[]];
                this.folderId = params.folderId;

                this.store.api.getNursingCarePlan({ voucherId: this.voucherId });
                this.store.api.getNursingCareDiagnoses(FilterBuilder.filter({ voucherNursingCareId: this.voucherId }));
                this.store.api.getNursingCareAssessments(FilterBuilder.filter({ voucherNursingCareId: this.voucherId }));
                this.store.api.getVoucher({ id: this.voucherId });
            }),

            this.store.data.nursingCarePlan$.subscribe((data) => {
                if (data) {
                    this.formSummary.patchValue({ summary: data.summary });
                }
            }),

            this.store.data.voucher$.subscribe((data) => {
                this.voucher = data;
                if (data && data.primaryDiagnosis) {
                    this.diagnosisLinkTitle = data.primaryDiagnosis.code + ' - ' + data.primaryDiagnosis.name;
                    const validFrom = localDate(data.issueDate, this.session, 'd.M.yyyy');
                    const validTo = localDate(data.validTo, this.session, 'd.M.yyyy');
                    this.title = this.translateService.instant('folder.voucher.nursingCarePlan.title', {
                        voucherId: data.id,
                        validFrom: validFrom,
                        validTo: validTo,
                        doctorIcp: data.doctor?.icpOfDoctor || '',
                    });
                    this.breadcrumbService.set(`@${BreadcrumbsEnum.FolderVoucherNursingCarePlan}`,
                        `${BreadcrumbsEnum.FolderVoucherNursingCarePlan}|` +
                        this.translateService.instant('folder.voucher.nursingCarePlan.breadcrumb', {
                            validFrom: validFrom,
                            validTo: validTo,
                        }),
                    );
                }
            }),
        );

        this.store.registerSuccessMessage('updateNursingCarePlan', 'folder.voucher.nursingCarePlan.updateSuccess');
    }

    cancel() {
        return this.router.navigate(['../../'], { relativeTo: this.route });
    }

    async submit() {
        this.invalidForm = false;
        if (isInvalid(this.formSummary)) {
            this.invalidForm = true;
            return;
        }

        const _data = this.formSummary.value;
        const data = {
            ..._data,
            voucherId: this.voucherId,
        } as VoucherNursingCarePlanUpdateInput;
        this.store.api.updateNursingCarePlan(data);

        this.wysiwyg()?.resetBackupValues();
    }

    protected deleteAssessment(event: any): void {
        this.confirmBuilder.confirmDelete(
            async () => {
                await this.store.api.deleteNursingCareAssessment({ id: event.item.id });
                successDeleted();
                this.assessmentsTable?.reload();
                this.diagnosesTable?.reload();
            },
            event,
        );
    }

    detailAssessment(assessment: VoucherNursingCareAssessmentGridItemObject) {
        this.openAssessmentDialog(assessment);
    }

    createAssessment() {
        this.openAssessmentDialog();
    }

    protected deleteNursingCare(event: any): void {
        this.confirmBuilder.confirmDelete(
            async () => {
                await this.store.api.deleteNursingCareDiagnosis({ id: event.item.id });
                successDeleted();
                this.assessmentsTable?.reload();
                this.diagnosesTable?.reload();
            },
            event,
        );
    }

    detailDiagnosis(diagnosis: VoucherNursingCareDiagnosisGridItemObject) {
        this.openDiagnosisDialog(diagnosis);
    }

    createDiagnosis() {
        this.openDiagnosisDialog();
    }

    showMainDiagnosis(show = true) {
        this.visibleMainDiagnosis = show;
    }

    private openDiagnosisDialog(diagnosis: VoucherNursingCareDiagnosisGridItemObject | null = null) {
        this.dialogService.open(
            NursingCareDiagnosisUpsertDialogComponent,
            {
                header: translate.instant(diagnosis ?
                    'folder.voucher.nursingCarePlan.createDiagnosis.updateTitle' :
                    'folder.voucher.nursingCarePlan.createDiagnosis.title'),
                styleClass: 'p-dialog-width-50',
                focusOnShow: false,
                data: {
                    voucherId: this.voucherId,
                    diagnosis,
                },
            },
        );
    }

    private openAssessmentDialog(assessment: VoucherNursingCareAssessmentGridItemObject | null = null) {
        this.dialogService.open(
            NursingCareAssessmentUpsertDialogComponent,
            {
                header: translate.instant(assessment ?
                    'folder.voucher.nursingCarePlan.createAssessment.updateTitle' :
                    'folder.voucher.nursingCarePlan.createAssessment.title'),
                styleClass: 'p-dialog-width-50',
                focusOnShow: false,
                data: {
                    voucher: this.voucher,
                    folderId: this.folderId,
                    assessment,
                },
            },
        );
    }
}
